import ButtonWithLink from '@gameday/react-components/components/ButtonWithLink'
import CountdownNotification from '@gameday/react-components/components/CountdownNotification'
import { StepperRegoForms } from '@gameday/react-components/components/StepperRegoForms'
import theme from '@gameday/react-components/utils/theme'
import { useEffect, useState } from 'react'
import { useBeforeunload } from 'react-beforeunload'
import styled from 'styled-components'

import { closeAllWindows } from '../../../../functions/popup'
import { getSortedFormType } from '../../../../functions/text'
import commonStyles from '../../../../styles/common-styles.module.scss'
import ErrorPage from '../ErrorPage'
import RegistrationFooter from '../RegistrationFooter'
import styles from './styles.scss'

const StyledWizardWrap = styled.div`
  width: 100%;
`

const ErrorMessage = styled.p`
  color: ${theme.colors.red};
  margin-bottom: 0;
`

const StyledFooter = styled.footer`
  border-top: 1px solid ${theme.borders.borderColorDark};
  background-color: ${theme.colors.white};
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`

const StyledButtonWrapper = styled.div`
  padding: 20px 15px 20px 20px;
  display: flex;
  gap: 10px;
  width: 100%;
`

const StyledErrorWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  width: 100%;
`

const StyledButtonRightWrapper = styled.div`
  margin-left: auto;
  display: flex;
  gap: 10px;
`

const StyledButtonLeftWrapper = styled.div`
  margin-right: auto;
  display: flex;
  gap: 10px;
`

const THIRTY_MINUTES = 1800
const EVENT_MESSAGE =
  'Please complete your registration within ' +
  `${Math.floor(THIRTY_MINUTES / 60)} minutes. The countdown will start when you proceed to the next screen.`
const VOUCHER_MESSAGE = `Please complete your registration within ${Math.floor(THIRTY_MINUTES / 60)} minutes.`
const SHOP_MESSAGE =
  'Please complete your purchase within ' +
  `${Math.floor(THIRTY_MINUTES / 60)} minutes. The countdown will start when you proceed to the next screen.`

const SummaryHeader = ({ headerColour = '#3FC0C3', headerTextColour = '#fff' }) => {
  const SummaryWrapper = styled.div`
    display: flex;
    align-items: center;
    flex-direction: column;
    height: 125px;
    width: 100%;
    background-color: ${theme.colors.teal};
    justify-content: center;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  `

  const StyledText = styled.span`
    color: ${headerTextColour};
    font-weight: ${theme.typography.fontWeightBolder};
    font-size: ${theme.typography.fontSizeBase};
    padding-top: 5px;
  `

  const TickIcon = ({ bgColor = '#fff', tickColor = '#3FC0C3' }) => {
    return (
      // biome-ignore lint/a11y/noSvgWithoutTitle: Unnecessary title
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" width="40" height="40">
        <circle cx="20" cy="20" r="16.25" fill={bgColor} />
        <path
          fill={tickColor}
          fill-rule="evenodd"
          d="M29.308 13.894a1.625 1.625 0 0 0-2.298 0L16.67 24.236l-4.597-4.596a1.625 1.625 0 1 0-2.298 2.298l5.746 5.745a1.621 1.621 0 0 0 2.298 0l11.49-11.49a1.625 1.625 0 0 0 0-2.299Z"
          clip-rule="evenodd"
        />
      </svg>
    )
  }

  return (
    <SummaryWrapper>
      <TickIcon bgColor={headerTextColour} tickColor={headerColour} />
      <StyledText>Payment Successful!</StyledText>
    </SummaryWrapper>
  )
}

const WizardPage = ({
  Component,
  next,
  previous,
  goto,
  currentStep,
  hideBackToLogin,
  orgLogo,
  headerColour,
  headerTextColour,
  buttonColour,
  buttonTextColour,
  steps,
  errorMessage,
  wide,
  logOutRequest,
  resetTimer,
  startTimer,
  onTimeup,
  match,
  history,
  messages,
  paymentGateway,
  isEvent,
  hasSubEvents,
  eventLogo,
  formInfo,
  subdomain,
  ...rest
}) => {
  const { isTeamEvent, resetForm, resetVoucher, teamData, registrations, formSettings } = rest
  const [isVoucherEnabledForm, setIsVoucherEnabledForm] = useState(false)
  const formType = getSortedFormType(formInfo?.form?.type)
  const countdownDuration = THIRTY_MINUTES
  const countdownMessage = isVoucherEnabledForm ? VOUCHER_MESSAGE : formType === 'shop' ? SHOP_MESSAGE : EVENT_MESSAGE

  const isSuccessPage = steps.length - 1
  const isPaymentPage = steps[currentStep]?.id === 'payment'
  const questionPage = steps.length - 3

  useEffect(() => {
    window.scrollTo(0, 0)
  })

  useBeforeunload(() => {
    closeAllWindows()
    return 'Changes you made may not be saved.'
  })

  const { organisation: formOrganisation, event } = formInfo || {}
  const { organisation: eventOrganisation } = event || {}

  const checkTeamLimitExceeded = (registeredTeam, registrations) => {
    if (!registeredTeam) {
      return false
    }

    const { teamDetails } = registeredTeam

    if (Object.keys(teamDetails).length !== 0) {
      const { participantsPerTeamLimit, teamParticipantCount } = teamDetails
      const current = Object.keys(registrations).length

      return teamParticipantCount + current >= participantsPerTeamLimit
    }
    return false
  }

  const renderStartNewRegistration = () => {
    return (
      <ButtonWithLink
        onClick={() => {
          resetForm()
          resetVoucher()
          isTeamEvent ? goto(0) : goto(-1)
        }}
        buttoncolour={buttonColour}
        buttontextcolour={buttonTextColour}
        disabled={checkTeamLimitExceeded(teamData, registrations)}
        label={formType === 'shop' ? 'Start New Shop Purchase' : 'Start New Registration'}
      />
    )
  }

  const renderViewEventInfo = () => {
    return (
      <a href={formSettings.landingPageLink} target="_blank" rel="noopener noreferrer">
        <ButtonWithLink buttoncolour={buttonColour} buttontextcolour={buttonTextColour} label="View Event Info" />
      </a>
    )
  }

  const renderDownloadApp = () => {
    return (
      <a href="https://mygameday.app/gameday-app/" target="_blank" rel="noopener noreferrer">
        <ButtonWithLink buttoncolour={buttonColour} buttontextcolour={buttonTextColour} label="Download the App" />
      </a>
    )
  }

  const renderViewMemberProfile = () => {
    const formMemberProfileLink = formInfo?.organisation?.memberProfileLink
    if (!formMemberProfileLink) {
      return null
    }

    return (
      <a href={formMemberProfileLink} target="_blank" rel="noopener noreferrer">
        <ButtonWithLink buttoncolour={buttonColour} buttontextcolour={buttonTextColour} label="View Member Profile" />
      </a>
    )
  }

  const renderError = () => {
    return (
      <>
        {checkTeamLimitExceeded(teamData, registrations) && (
          <ErrorMessage>Maximum participants for this team reached</ErrorMessage>
        )}
      </>
    )
  }

  const renderButtons = () => {
    const formMemberProfileLink = formInfo?.organisation?.memberProfileLink
    if (formType === 'membership' || formType === 'shop') {
      return (
        <>
          <StyledButtonLeftWrapper>{renderDownloadApp()}</StyledButtonLeftWrapper>
          <StyledButtonRightWrapper>
            {renderStartNewRegistration()}
            {renderViewMemberProfile()}
          </StyledButtonRightWrapper>
        </>
      )
    }

    if (formType !== 'membership' && formType !== 'shop') {
      return (
        <>
          <StyledButtonLeftWrapper>{renderDownloadApp()}</StyledButtonLeftWrapper>
          <StyledButtonRightWrapper>
            {renderStartNewRegistration()}
            {renderViewEventInfo()}
            {formMemberProfileLink && renderViewMemberProfile()}
          </StyledButtonRightWrapper>
        </>
      )
    }

    return
  }

  let renderPage = null
  if (errorMessage) {
    renderPage = (
      <ErrorPage errorDescription={errorMessage} hideBackToLogin={hideBackToLogin} logOutRequest={logOutRequest} />
    )
  } else {
    renderPage = (
      <StyledWizardWrap>
        {startTimer && (
          <div className={styles.timerContainer}>
            <CountdownNotification
              defaultMessage={countdownMessage}
              errorMessage="Time is up!"
              reset={resetTimer}
              startTimer={startTimer}
              time={countdownDuration}
              onTimeup={onTimeup}
            />
          </div>
        )}
        <div className={commonStyles.pageContainer}>
          {steps[currentStep]?.id !== 'success' ? (
            <div className={styles.sticky}>
              <StepperRegoForms
                activeStep={currentStep}
                steps={steps.map(s => s.title)}
                className={styles.stepper}
                labelClass={styles.stepperLabel}
                headercolour={headerColour}
                headertextcolour={headerTextColour}
              />
            </div>
          ) : (
            <SummaryHeader headercolour={headerColour} headertextcolour={headerTextColour} />
          )}

          <div className={styles.fieldsetContainer} style={wide ? { maxWidth: '600px' } : null}>
            <Component
              next={next}
              previous={previous}
              goto={goto}
              steps={steps}
              buttonColour={buttonColour}
              buttonTextColour={buttonTextColour}
              match={match}
              history={history}
              setIsVoucherEnabledForm={setIsVoucherEnabledForm}
              isVoucherEnabledForm={isVoucherEnabledForm}
              hasSubEvents={hasSubEvents}
              successPage={isSuccessPage}
              questionPage={questionPage}
              formInfo={formInfo}
              {...rest}
            />
          </div>

          {steps[currentStep]?.id === 'success' && (
            <StyledFooter>
              <StyledErrorWrapper>{renderError()}</StyledErrorWrapper>
              <StyledButtonWrapper>{renderButtons()}</StyledButtonWrapper>
            </StyledFooter>
          )}
        </div>
        {!isPaymentPage && (
          <RegistrationFooter
            isEvent={isEvent}
            logo={isEvent ? eventLogo : orgLogo}
            event={event}
            organisation={isEvent ? eventOrganisation : formOrganisation}
            paymentGateway={paymentGateway}
            subdomain={subdomain}
          />
        )}
      </StyledWizardWrap>
    )
  }

  return <>{renderPage}</>
}

WizardPage.defaultProps = {
  wide: false,
  errorMessage: null,
  orgLogo: null,
  headerColour: theme.header.background,
  headerTextColour: theme.header.textColor,
  buttonColour: theme.buttons.primaryButtonBg,
  buttonTextColour: theme.buttons.primaryButtonColor,
  currentStep: -1,
  hideBackToLogin: false,
  resetTimer: false,
  startTimer: false,
  onTimeup: () => {},
}

export default WizardPage
